import { Link, Navigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Container } from '@/components/ui/Container';
import { Seo } from '@/components/seo/Seo';
import { MarkdownContent } from '@/components/blog/MarkdownContent';
import { usePost } from '@/hooks/usePost';
import { usePageViewTracking } from '@/hooks/usePageViewTracking';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { authors } from '@/data/authors';
import { PostList } from '@/components/blog/PostList';

export const PostPage = (): React.JSX.Element => {
  const { slug = '' } = useParams();
  const { post, related, isLoading, error } = usePost(slug);
  const { config, isLoading: configLoading } = useSiteConfig();
  usePageViewTracking({
    enabled: !configLoading && !isLoading && !error && Boolean(post),
    entryOnly: config.analytics.entryOnly,
    requireUtm: config.analytics.requireUtm,
    slug: post?.slug,
    title: post?.title,
  });

  if (isLoading) {
    return (
      <Container>
        <p style={{ padding: '4rem 0', textAlign: 'center', color: 'var(--cream-3)' }}>
          Loading…
        </p>
      </Container>
    );
  }

  if (error || !post) {
    return <Navigate to="/404" replace />;
  }

  const author = authors[post.author as keyof typeof authors];

  return (
    <>
      <Seo title={post.title} description={post.description} path={`/posts/${post.slug}`} />
      <Container>
        <article className="article-shell">
          <header className="article-header">
            <p className="article-header__eyebrow">{post.category}</p>
            <h1>{post.title}</h1>
            <p className="article-header__dek">{post.description}</p>
            <div className="article-header__meta">
              <span>{format(new Date(post.date), 'dd MMMM yyyy')}</span>
              <span>{post.readingTime}</span>
              {author ? <span>{author.name}</span> : null}
            </div>
          </header>

          <div className="article-layout">
            <div className="article-content">
              <MarkdownContent
                content={post.content}
                context={{ slug: post.slug, title: post.title }}
              />
            </div>

            <aside className="article-sidebar">
              {author ? (
                <section className="sidebar-card">
                  <h2>Author</h2>
                  <p>{author.name}</p>
                  <p>{author.role}</p>
                  <p>{author.bio}</p>
                </section>
              ) : null}

              <section className="sidebar-card">
                <h2>Tags</h2>
                <div className="tag-cloud">
                  {post.tags.map((tag) => (
                    <Link key={tag} className="tag-cloud__item" to={`/tags/${tag.toLowerCase()}`}>
                      #{tag}
                    </Link>
                  ))}
                </div>
              </section>
            </aside>
          </div>
        </article>

        {related.length ? (
          <section className="section-stack">
            <div className="section-heading">
              <h2>Related posts</h2>
            </div>
            <PostList posts={related} />
          </section>
        ) : null}
      </Container>
    </>
  );
};
